import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  CreditCard,
  Building2,
  Wallet,
  CheckCircle2,
  Copy,
  ArrowLeft,
  Shield,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_labresult-hub-1/artifacts/td8pjg41_FACTUAL%20LOGO.png";

const BookingPaymentSummary = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [bankDetails, setBankDetails] = useState(null);

  useEffect(() => {
    fetchBookingDetails();
    fetchBankDetails();
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data);
      
      // Fetch test details
      if (response.data.test_ids?.length > 0) {
        const testsRes = await axios.get(`${API}/tests`);
        const bookingTests = testsRes.data.filter(t => response.data.test_ids.includes(t.id));
        setTests(bookingTests);
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast.error('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const fetchBankDetails = async () => {
    try {
      const response = await axios.get(`${API}/bank-transfer-details`);
      setBankDetails(response.data);
    } catch (error) {
      console.error('Error fetching bank details:', error);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/payments/create-checkout?booking_id=${bookingId}&origin_url=${window.location.origin}&gateway=${paymentMethod}`
      );
      
      if (paymentMethod === 'bank_transfer') {
        navigate(`/payment-success?gateway=bank_transfer&reference=${response.data.reference}&booking_id=${bookingId}`);
      } else if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to process payment');
      setProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-96">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  if (!booking) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <h2 className="text-xl font-semibold text-foreground">Booking not found</h2>
          <Button className="mt-4" onClick={() => navigate('/bookings')}>
            View All Bookings
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="payment-summary-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6 text-muted-foreground"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Order Summary */}
          <div className="lg:col-span-2 space-y-6">
            <div className="card-clinical">
              <div className="flex items-center gap-4 p-6 border-b border-border">
                <img src={LOGO_URL} alt="Logo" className="w-12 h-12 rounded-full" />
                <div>
                  <h1 className="text-xl font-bold text-foreground">Order Summary</h1>
                  <p className="text-sm text-muted-foreground">Booking #{booking.id?.slice(0, 8).toUpperCase()}</p>
                </div>
              </div>

              {/* Appointment Details */}
              <div className="p-6 space-y-4">
                <h2 className="font-semibold text-foreground mb-4">Appointment Details</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Calendar className="w-5 h-5 text-sky-600" />
                    <div>
                      <p className="text-xs text-muted-foreground">Date</p>
                      <p className="font-medium text-foreground">{booking.appointment_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Clock className="w-5 h-5 text-sky-600" />
                    <div>
                      <p className="text-xs text-muted-foreground">Time</p>
                      <p className="font-medium text-foreground">{booking.appointment_time}</p>
                    </div>
                  </div>
                </div>
                {booking.address && (
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <MapPin className="w-5 h-5 text-sky-600" />
                    <div>
                      <p className="text-xs text-muted-foreground">Location</p>
                      <p className="font-medium text-foreground">{booking.address || 'In-Centre'}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Tests */}
              <div className="p-6 border-t border-border">
                <h2 className="font-semibold text-foreground mb-4">Tests Ordered</h2>
                <div className="space-y-3">
                  {tests.map((test) => (
                    <div key={test.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                      <div>
                        <p className="font-medium text-foreground">{test.name}</p>
                        <p className="text-xs text-muted-foreground">{test.category}</p>
                      </div>
                      <p className="font-semibold text-foreground">₦{test.price?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="card-clinical p-6">
              <h2 className="font-semibold text-foreground mb-4">Select Payment Method</h2>
              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
                <div className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${paymentMethod === 'bank_transfer' ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20' : 'border-border'}`}>
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value="bank_transfer" id="bank_transfer" />
                    <Label htmlFor="bank_transfer" className="cursor-pointer flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-sky-600" />
                      <div>
                        <p className="font-medium text-foreground">Bank Transfer</p>
                        <p className="text-xs text-muted-foreground">Transfer to our bank account</p>
                      </div>
                    </Label>
                  </div>
                </div>
                <div className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${paymentMethod === 'paystack' ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20' : 'border-border'}`}>
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value="paystack" id="paystack" />
                    <Label htmlFor="paystack" className="cursor-pointer flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="font-medium text-foreground">Paystack</p>
                        <p className="text-xs text-muted-foreground">Pay with card, bank, or USSD</p>
                      </div>
                    </Label>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">Recommended</Badge>
                </div>
              </RadioGroup>

              {/* Bank Details (if bank transfer selected) */}
              {paymentMethod === 'bank_transfer' && bankDetails && (
                <div className="mt-4 p-4 bg-sky-50 dark:bg-sky-900/20 rounded-lg border border-sky-200 dark:border-sky-800">
                  <h3 className="font-medium text-foreground mb-3">Bank Account Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Bank Name:</span>
                      <span className="font-medium text-foreground">{bankDetails.bank_name}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Account Number:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-foreground">{bankDetails.account_number}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0"
                          onClick={() => copyToClipboard(bankDetails.account_number)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Account Name:</span>
                      <span className="font-medium text-foreground">{bankDetails.account_name}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Price Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="card-clinical p-6 sticky top-24">
              <h2 className="font-semibold text-foreground mb-4">Price Summary</h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal ({tests.length} test{tests.length > 1 ? 's' : ''})</span>
                  <span className="text-foreground">₦{booking.total_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span className="text-foreground">₦0.00</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Discount</span>
                  <span className="text-emerald-600">-₦0.00</span>
                </div>
              </div>

              <div className="border-t border-border pt-4 mb-6">
                <div className="flex justify-between">
                  <span className="font-semibold text-foreground">Total</span>
                  <span className="text-2xl font-bold text-sky-600">
                    ₦{booking.total_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              <Button 
                className="w-full bg-sky-600 hover:bg-sky-700 h-12"
                onClick={handlePayment}
                disabled={processing}
                data-testid="proceed-to-pay-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    {paymentMethod === 'bank_transfer' ? 'Confirm & Get Details' : 'Proceed to Pay'}
                  </>
                )}
              </Button>

              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <Shield className="w-4 h-4" />
                <span>Secure payment processing</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BookingPaymentSummary;
